import { useTranslation } from "react-i18next";
import { Library, ClipboardCheck, Activity, FileText, Calculator, BookOpen, HelpCircle, Map, Mountain, ArrowRight, Presentation, Video, Calendar, Compass } from "lucide-react";
import LocaleLink from "@/components/LocaleLink";

interface MegaMenuResourcesProps {
  onItemClick?: () => void;
}

interface FunnelItem {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  desc: string;
  step?: number;
}

const MegaMenuResources = ({ onItemClick }: MegaMenuResourcesProps) => {
  const { t } = useTranslation();

  const funnel: FunnelItem[] = [
    {
      to: "/readiness",
      icon: ClipboardCheck,
      title: t('megaMenu.readinessAssessment'),
      desc: t('megaMenu.readinessAssessmentDescStep'),
      step: 1,
    },
    {
      to: "/compliance-blueprint",
      icon: Map,
      title: t('megaMenu.blueprintTitle'),
      desc: t('megaMenu.blueprintDescStep'),
      step: 2,
    },
    {
      to: "/roi-calculator",
      icon: Calculator,
      title: t('megaMenu.roiCalculator'),
      desc: t('megaMenu.roiCalculatorDescStep'),
      step: 3,
    },
    // The title on this entry used to read "R.I.P.E. Assessment" while the
    // link went to /assessment, a different tool, and the real R.I.P.E.
    // assessment was in neither the nav nor the footer. The description
    // ("Deep-dive maturity assessment") was always the accurate half.
    {
      to: "/assessment",
      icon: Activity,
      title: t('megaMenu.complianceAssessment'),
      desc: t('megaMenu.ripeAssessmentDescDeepDive'),
    },
    {
      to: "/ripe-assessment",
      icon: Compass,
      title: t('megaMenu.ripeAssessment'),
      desc: t('megaMenu.ripeAssessmentDescTrailhead'),
    },
  ];

  return (
    <div className="w-[780px] p-6">
      <div className="grid grid-cols-3 gap-6">
        <div>
          <h3 className="eyebrow mb-4">
            {t('megaMenu.startHerePath')}
          </h3>
          <div className="space-y-1">
            {funnel.map((item) => {
              const Icon = item.icon;
              return (
                <LocaleLink
                  key={item.to}
                  to={item.to}
                  onClick={onItemClick}
                  className="flex items-start gap-3 p-3 hover:bg-secondary transition-colors group"
                >
                  <Icon className="h-5 w-5 text-accent mt-0.5" />
                  <div className="min-w-0">
                    <div className="font-medium text-sm group-hover:text-primary">
                      {item.title}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {item.step !== undefined && (
                        <span className="font-mono text-muted-foreground mr-1.5">
                          {String(item.step).padStart(2, "0")} —
                        </span>
                      )}
                      {item.desc}
                    </div>
                  </div>
                </LocaleLink>
              );
            })}
          </div>
        </div>
        <div>
          <h3 className="eyebrow mb-4">
            {t('megaMenu.learn')}
          </h3>
          <div className="space-y-1">
            <LocaleLink to="/insights" onClick={onItemClick} className="flex items-start gap-3 p-3 hover:bg-secondary transition-colors group">
              <FileText className="h-5 w-5 text-accent mt-0.5" />
              <div>
                <div className="font-medium text-sm group-hover:text-primary">{t('megaMenu.insightsResources')}</div>
                <div className="text-xs text-muted-foreground">{t('megaMenu.insightsResourcesDesc')}</div>
              </div>
            </LocaleLink>
            <LocaleLink to="/resources" onClick={onItemClick} className="flex items-start gap-3 p-3 hover:bg-secondary transition-colors group">
              <BookOpen className="h-5 w-5 text-accent mt-0.5" />
              <div>
                <div className="font-medium text-sm group-hover:text-primary">{t('megaMenu.resourceCenter')}</div>
                <div className="text-xs text-muted-foreground">{t('megaMenu.resourceCenterDesc')}</div>
              </div>
            </LocaleLink>
            <LocaleLink to="/faq" onClick={onItemClick} className="flex items-start gap-3 p-3 hover:bg-secondary transition-colors group">
              <HelpCircle className="h-5 w-5 text-accent mt-0.5" />
              <div>
                <div className="font-medium text-sm group-hover:text-primary">{t('megaMenu.faq')}</div>
                <div className="text-xs text-muted-foreground">{t('megaMenu.faqDesc')}</div>
              </div>
            </LocaleLink>
            <div className="p-3 hover:bg-secondary transition-colors">
              <a href="https://docs.csquaregrc.com/" onClick={onItemClick} className="flex items-start gap-3 group">
                <Library className="h-5 w-5 text-accent mt-0.5" />
                <div>
                  <div className="font-medium text-sm group-hover:text-primary">{t('megaMenu.docs')}</div>
                  <div className="text-xs text-muted-foreground">{t('megaMenu.docsDesc')}</div>
                </div>
              </a>
              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 pl-8 text-xs">
                <a href="https://docs.csquaregrc.com/getting-started" onClick={onItemClick} className="text-primary hover:underline">{t('megaMenu.docsGettingStarted')}</a>
                <a href="https://docs.csquaregrc.com/connectors" onClick={onItemClick} className="text-primary hover:underline">{t('megaMenu.docsConnectors')}</a>
                <a href="https://docs.csquaregrc.com/api" onClick={onItemClick} className="text-primary hover:underline">API</a>
              </div>
            </div>
            <LocaleLink to="/hikers-mindset" onClick={onItemClick} className="flex items-start gap-3 p-3 hover:bg-secondary transition-colors group">
              <Mountain className="h-5 w-5 text-accent mt-0.5" />
              <div>
                <div className="font-medium text-sm group-hover:text-primary">{t('megaMenu.hikersMindset')}</div>
                <div className="text-xs text-muted-foreground">{t('megaMenu.hikersMindsetDesc')}</div>
              </div>
            </LocaleLink>
          </div>
        </div>
        <div>
          <h3 className="eyebrow mb-4">
            {t('megaMenu.events')}
          </h3>
          <div className="space-y-1">
            <LocaleLink to="/demo-days" onClick={onItemClick} className="flex items-start gap-3 p-3 hover:bg-secondary transition-colors group">
              <Presentation className="h-5 w-5 text-accent mt-0.5" />
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm group-hover:text-primary">
                    {t('megaMenu.demoDays')}
                  </span>
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-none text-[9px] font-bold tracking-wider bg-primary text-primary-foreground">
                    {t('megaMenu.newPill')}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {t('megaMenu.demoDaysDesc')}
                </div>
              </div>
            </LocaleLink>
            {/* The video walkthrough now lives on /platform-tour, which the
                Platform menu already points at. Two entries called "tour" in
                two menus was the reason to merge the pages; keeping one here
                would have kept the ambiguity. */}
            <LocaleLink to="/webinars" onClick={onItemClick} className="flex items-start gap-3 p-3 hover:bg-secondary transition-colors group">
              <Video className="h-5 w-5 text-accent mt-0.5" />
              <div>
                <div className="font-medium text-sm group-hover:text-primary">
                  {t('megaMenu.webinars')}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t('megaMenu.webinarsDesc')}
                </div>
              </div>
            </LocaleLink>
            <LocaleLink to="/events" onClick={onItemClick} className="flex items-start gap-3 p-3 hover:bg-secondary transition-colors group">
              <Calendar className="h-5 w-5 text-accent mt-0.5" />
              <div>
                <div className="font-medium text-sm group-hover:text-primary">
                  {t('megaMenu.allEvents')}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t('megaMenu.allEventsDesc')}
                </div>
              </div>
            </LocaleLink>
          </div>
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-border flex items-center justify-between gap-3">
        <span className="text-xs text-muted-foreground">{t('megaMenu.readyToTalk')}</span>
        <LocaleLink
          to="/book-demo"
          onClick={onItemClick}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
        >
          {t('megaMenu.bookADemo')}
          <ArrowRight className="h-4 w-4" />
        </LocaleLink>
      </div>
    </div>
  );
};

export default MegaMenuResources;
